import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../components/ui';

// design/029 — superadmin moderation for Event / Group conversations
// (App Store 1.2). Reports queue (oldest first, with age) + actions, and the
// blocked-words list the gateway filter uses.
const errText = (e) => (e?.data?.message || e?.message || 'Something went wrong').replace(/^[A-Z_]+:\s*/, '');

const REASON_LABEL = { HARASSMENT: 'Harassment', SPAM: 'Spam', INAPPROPRIATE: 'Inappropriate', OTHER: 'Other' };
const RESOLUTION_LABEL = {
  DISMISSED: 'Dismissed — content OK', HIDDEN: 'Post removed', REMOVED_USER: 'Removed from community', LEADER: 'Handled by leader',
};

export default function AdminModerationPage() {
  const [tab, setTab] = useState('open');
  const tabs = [
    { key: 'open', label: 'Open reports' },
    { key: 'resolved', label: 'Resolved' },
    { key: 'terms', label: 'Blocked words' },
  ];
  return (
    <div className="page">
      <PageHead icon="⚑" title="Moderation"
        sub="Reported conversation posts and the blocked-words filter. We act on every report within 24 hours." />
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, borderBottom: '1px solid var(--border)' }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : ''}`}
            style={{ borderRadius: '8px 8px 0 0', marginBottom: -1 }}>{t.label}</button>
        ))}
      </div>
      {tab === 'terms' ? <BlockedWords /> : <Reports status={tab} />}
    </div>
  );
}

// ---- Reports ----------------------------------------------------------------

function age(iso) {
  if (!iso) return { text: '—', hours: 0 };
  const h = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (h < 1) return { text: `${Math.max(1, Math.round(h * 60))} min`, hours: h };
  if (h < 48) return { text: `${Math.round(h)} h`, hours: h };
  return { text: `${Math.round(h / 24)} days`, hours: h };
}

function AgeBadge({ iso }) {
  const a = age(iso);
  const bg = a.hours > 24 ? '#fee2e2' : a.hours > 12 ? '#fef3c7' : '#f3f4f6';
  const fg = a.hours > 24 ? '#991b1b' : a.hours > 12 ? '#92400e' : 'var(--muted)';
  return <span className="pill" style={{ background: bg, color: fg, fontWeight: 700 }}>waiting {a.text}</span>;
}

function postText(r) {
  if (r.kind === 'REFLECTION') {
    return [['Observation', r.observation], ['Feelings', r.feelings], ['Needs', r.needs], ['Request', r.request]]
      .filter(([, v]) => v);
  }
  return r.body ? [[null, r.body]] : [];
}

function Reports({ status }) {
  const open = status === 'open';
  const { loading, error, data, reload } = useAsync(
    () => api.modReports(status).then((r) => (Array.isArray(r) ? r : [])), [status]);
  const [busy, setBusy] = useState(null);

  const act = async (r, action) => {
    if (action === 'remove') {
      const ok = window.confirm(
        `Remove ${r.author?.name || 'this user'} from the community?\n\n`
        + '• All their posts and replies are hidden\n'
        + '• They are removed from every group and event\n'
        + '• Their account is suspended: when they sign in they only see why, and can delete their account\n'
        + '• Their web subscription stops renewing (no refund)\n'
        + '• Their email can\'t be used for a new account');
      if (!ok) return;
    }
    setBusy(`${r.postId}:${action}`);
    try {
      if (action === 'dismiss') await api.modDismiss(r.postId);
      else if (action === 'hide') await api.modHide(r.postId);
      else await api.modRemoveUser(r.postId);
      await reload();
    } catch (e) {
      alert(errText(e));
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorNote error={error} onRetry={reload} />;
  if (data.length === 0) {
    return <Section><Empty>{open ? 'No open reports. 🎉' : 'No resolved reports yet.'}</Empty></Section>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {data.map((r) => {
        const isEvent = r.scopeType === 'EVENT';
        const link = isEvent ? `/event/${r.scopeId}` : `/group/${r.scopeId}`;
        return (
          <Section key={r.postId}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <span className="pill" style={{ background: isEvent ? '#fff7ed' : '#ecfdf5', color: isEvent ? '#c2410c' : '#15803d', fontWeight: 700 }}>
                {isEvent ? 'Event' : 'Group'}
              </span>
              <Link to={link} style={{ fontWeight: 600 }}>{r.boardName || (isEvent ? 'Event' : 'Group')}</Link>
              {r.isReply && <span className="pill">reply</span>}
              {r.hidden && <span className="pill" style={{ background: '#fee2e2', color: '#991b1b' }}>
                hidden{r.hiddenReason === 'AUTO_REPORTED' ? ' (auto, 3+ reports)' : ''}
              </span>}
              <span style={{ flex: 1 }} />
              {open ? <AgeBadge iso={r.firstReportedAt} />
                : <span className="pill">{RESOLUTION_LABEL[r.resolution] || r.resolution || 'Resolved'}</span>}
            </div>

            <div style={{ background: 'var(--card-soft, #f9fafb)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, whiteSpace: 'pre-wrap' }}>
              {postText(r).length === 0 ? <i style={{ color: 'var(--muted)' }}>(no text)</i>
                : postText(r).map(([label, text], i) => (
                  <div key={i} style={{ marginBottom: 4 }}>{label && <b>{label}: </b>}{text}</div>
                ))}
            </div>

            <div style={{ marginTop: 10, fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.6 }}>
              <div>
                <b style={{ color: 'var(--ink)' }}>{r.author?.name || 'Unknown'}</b>
                {r.author?.email ? ` · ${r.author.email}` : ''}
                {r.author?.removed && <span className="pill" style={{ marginLeft: 6, background: '#fee2e2', color: '#991b1b' }}>removed</span>}
              </div>
              <div>
                {r.reportCount} report{r.reportCount === 1 ? '' : 's'}
                {r.reasons?.length ? ` · ${r.reasons.map((x) => REASON_LABEL[x] || x).join(', ')}` : ''}
                {r.firstReportedAt ? ` · first ${new Date(r.firstReportedAt).toLocaleString()}` : ''}
                {!open && r.resolvedAt ? ` · resolved ${new Date(r.resolvedAt).toLocaleString()}` : ''}
              </div>
            </div>

            {r.reporters?.length > 0 && (
              <div style={{ marginTop: 8, fontSize: '0.85rem' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Reported by</div>
                {r.reporters.map((p) => (
                  <div key={`${p.id}-${p.at}`} style={{ color: 'var(--muted)', lineHeight: 1.6 }}>
                    <b style={{ color: 'var(--ink)' }}>{p.name}</b>
                    {p.username ? ` @${p.username}` : ''}{p.email ? ` · ${p.email}` : ''}
                    {' · '}{REASON_LABEL[p.reason] || p.reason}
                    {p.at ? ` · ${new Date(p.at).toLocaleString()}` : ''}
                    {p.note && <div style={{ marginLeft: 12 }}>“{p.note}”</div>}
                  </div>
                ))}
              </div>
            )}

            {open && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <button className="btn btn-sm" disabled={!!busy} onClick={() => act(r, 'dismiss')}>
                  {busy === `${r.postId}:dismiss` ? 'Dismissing…' : 'Dismiss — content is OK'}
                </button>
                <button className="btn btn-sm" disabled={!!busy || (r.hidden && r.hiddenReason !== 'AUTO_REPORTED')} onClick={() => act(r, 'hide')}>
                  {busy === `${r.postId}:hide` ? 'Removing…' : 'Remove post'}
                </button>
                <button className="btn btn-sm" disabled={!!busy || r.author?.removed}
                  style={{ color: '#b91c1c', borderColor: '#fca5a5' }} onClick={() => act(r, 'remove')}>
                  {busy === `${r.postId}:remove` ? 'Removing…' : 'Remove from community'}
                </button>
              </div>
            )}
          </Section>
        );
      })}
    </div>
  );
}

// ---- Blocked words ----------------------------------------------------------

function BlockedWords() {
  const { loading, error, data, reload } = useAsync(
    () => api.modTerms().then((r) => (Array.isArray(r) ? r : [])), []);
  const [term, setTerm] = useState('');
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  const add = async (e) => {
    e.preventDefault();
    if (!term.trim()) return;
    setBusy(true);
    try { await api.modAddTerm(term.trim()); setTerm(''); await reload(); }
    catch (err) { alert(errText(err)); }
    finally { setBusy(false); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Remove “${t.term}” from the blocked words?`)) return;
    try { await api.modDeleteTerm(t.id); await reload(); }
    catch (err) { alert(errText(err)); }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorNote error={error} onRetry={reload} />;
  const shown = q.trim() ? data.filter((t) => t.term.includes(q.trim().toLowerCase())) : data;

  return (
    <Section>
      <p style={{ marginTop: 0, color: 'var(--muted)', fontSize: '0.875rem' }}>
        Posts and replies containing any of these are refused before they’re saved. Single words also block common
        endings (-s, -ed, -er, -ing, -y…) and simple disguises like <code>f.u.c.k</code> or <code>@</code> for “a”.
        Words people use when honestly sharing hard experiences (e.g. abuse, suicide, hate) are left off on purpose.
      </p>
      <form onSubmit={add} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input className="input" style={{ flex: 1 }} value={term} onChange={(e) => setTerm(e.target.value)}
          placeholder="Add a word or phrase…" maxLength={100} />
        <button className="btn btn-primary" disabled={busy || !term.trim()}>{busy ? 'Adding…' : 'Add'}</button>
      </form>
      <input className="input" style={{ width: '100%', marginBottom: 10 }} value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${data.length} blocked words…`} />
      {shown.length === 0 ? <Empty>No matching words.</Empty> : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {shown.map((t) => (
            <span key={t.id} className="pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {t.term}{t.matchType === 'PHRASE' ? ' (phrase)' : ''}
              <button type="button" onClick={() => remove(t)} aria-label={`Remove ${t.term}`}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      )}
    </Section>
  );
}
